import {
	ReactElement,
	ReactNode,
	useEffect,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	DateString,
	DetailsBox,
	DurationString,
	Space,
	WeekViewCardStyle
} from "client/Components";
import {
	NedapOnsEventKind,
	NedapOnsEventListItem,
	NedapOnsShiftDetails,
	getShiftDetails,
	NedapOnsUnavailabilityListItem
} from "client/Api/Workspace/LiveInformation/NedapOns/nedapOnsLiveInformation";
import { TimeZone } from "client/DateAndTime/timezones";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";

export type EventDetailsProps = {
	connectionId: number;
	item: NedapOnsEventListItem;
	onClose(): void;
	variant: WeekViewCardStyle | undefined;
	timezone: TimeZone;
};

export const EventDetails = (props: EventDetailsProps): ReactElement => {
	const {
		connectionId,
		item,
		onClose,
		variant,
		timezone
	} = props;

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [details, setDetails] = useState<NedapOnsShiftDetails | null>(null);

	useEffect(
		() => {

			if (item.kind !== NedapOnsEventKind.Shift) {
				setIsLoading(false);
				return undefined;
			}

			const load = async (signal: AbortSignal): Promise<void> => {
				setIsLoading(true);
				try {
					const record: NedapOnsShiftDetails = await getShiftDetails(
						connectionId,
						item.id,
						parseDtoDate(item.start),
						timezone,
						signal
					);
					setDetails(record);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					console.error(e);
				}

				setIsLoading(false);
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return () => cancellation.abort();
		},
		[
			connectionId,
			item,
			timezone
		]
	);

	const renderEvent = (): ReactNode => {
		if (item.kind === NedapOnsEventKind.Shift) {
			return details
				? (
					<ShiftDetails
						shift={details}
						timezone={timezone}
					/>
				)
				: (
					<DetailsBox.Item icon="triangle-exclamation">
						{localize("_UnknownError")}
					</DetailsBox.Item>
				);
		}

		return (
			<UnavailableDetails
				item={item}
				timezone={timezone}
			/>
		);
	};

	return (
		<DetailsBox
			variant={variant}
			title={item.title}
			onClose={onClose}
			hasBusyIndicator={isLoading}
		>
			<DetailsBox.List>
				{!isLoading && renderEvent()}
			</DetailsBox.List>
		</DetailsBox>
	);
};
EventDetails.displayName = "EventDetails";

type ShiftDetailsProps = {
	shift: NedapOnsShiftDetails;
	timezone: TimeZone;
};

const ShiftDetails = (props: ShiftDetailsProps): ReactElement => {
	const {
		shift,
		timezone
	} = props;

	const {
		duration,
		start,
		end,
		team,
		code,
		expertiseProfile
	} = shift;

	return (
		<>
			<DetailsBox.Item icon="user-group">
				{team}
			</DetailsBox.Item>
			<DetailsBox.Item icon="calendar-circle-user">
				{code}
			</DetailsBox.Item>
			<DetailsBox.Item icon="syringe">
				{expertiseProfile}
			</DetailsBox.Item>
			<DetailsBox.Item icon="hourglass-end">
				<DurationString seconds={duration * 60} />
			</DetailsBox.Item>
			<DetailsBox.Item icon="clock">
				<DateString
					value={start}
					format="T"
					timezone={timezone}
				/>
				<Space />-<Space />
				<DateString
					value={end}
					format="T"
					timezone={timezone}
				/>
			</DetailsBox.Item>
		</>
	);
};
ShiftDetails.displayName = "ShiftDetails";

type UnavailableDetailsProps = {
	item: NedapOnsUnavailabilityListItem;
	timezone: TimeZone;
};

const UnavailableDetails = (props: UnavailableDetailsProps): ReactElement => {
	const {
		item,
		timezone
	} = props;

	const {
		start,
		end
	} = item;

	return (
		<>
			<DetailsBox.Item icon="clock">
				<DateString
					value={start}
					format="T"
					timezone={timezone}
				/>
				<Space />-<Space />
				<DateString
					value={end}
					format="T"
					timezone={timezone}
				/>
			</DetailsBox.Item>
		</>
	);
};
UnavailableDetails.displayName = "UnavailableDetails";
